/**
 * Clearline redesign of the property cases list — mirrors CasesRedesign.js's
 * shell/visual language but is its own component, since property calls a
 * wholly separate API surface (propertyApiService.js).
 *
 * The property list page calls exactly four endpoints:
 * /assessment_list/all, /client_demo, /client_config and /useridList —
 * notably NOT /assessment_report, which the motor list uses for its stats.
 * "Total Inspections" comes from total_countOfClaims on the list response.
 * There is no status filter (property's list endpoint takes no claim_state)
 * and search filters loaded rows client-side (no /search in this flow).
 *
 * `detailsBasePath` mirrors CasesRedesign's prop so rows link to the
 * redesigned property detail page.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { AlertTriangle, Building2, ChevronDown, Search, X } from "lucide-react";
import { encodeRefNum } from "../utils/refNumCrypto";
import { withCountryParam } from "../utils/caseHref";
import CaseThumb from "../components/CaseThumb";
import {
  getPropertyAssessmentList,
  getPropertyClientDemo,
  getPropertyClientConfig,
  getPropertyUseridList,
} from "../services/propertyApiService";
import {
  toPropertyList,
  filterRowsByCountry,
} from "../services/adapters/propertyAssessment";
import { getClearlineStatusChip, getStatusInfo, formatClearlineDateTime } from "../utils/statusMapping";
import { summarizePropertyCaseForCammy } from "../utils/cammyPropertyCaseSummary";
import { useCammySay } from "../context/CammySpeechContext";

import latviaFlag from "../assets/icons/cases/latvia-flag.svg";
import lithuaniaFlag from "../assets/icons/cases/lithuania-flag.svg";
import estoniaFlag from "../assets/icons/cases/estonia-flag.svg";
import loadingIcon from "../assets/icons/cases/loading.svg";

const flagClass =
  "h-[13px] w-[18px] flex-none rounded-[2px] object-cover shadow-[0_0_0_1px_rgba(0,0,0,0.08)]";

const FLAGS = {
  latvia: latviaFlag,
  lv: latviaFlag,
  lithuania: lithuaniaFlag,
  lt: lithuaniaFlag,
  estonia: estoniaFlag,
  ee: estoniaFlag,
};

const CountryFlag = ({ country }) => {
  const src = FLAGS[country?.toLowerCase().trim()];
  if (!src) return null;
  return <img src={src} alt="" className={flagClass} />;
};

// Motor's Cases.js sends this lowercase and its backend accepts it. Property
// rows come back with country_corp_id spelled "Estonia"/"Lithuania" — Title
// Case, full name (see the real /assessment_list/all sample in
// propertyAssessment.test.js) — and the property API is a different backend
// from motor's, so there's no reason to assume it normalises case the same
// way. Sent here in the one form actually observed on this data.
const getCountryApiValue = (countryName) => {
  const country = countryName?.toLowerCase().trim();
  if (["latvia", "lv", "lat"].includes(country)) return "Latvia";
  if (["lithuania", "lt", "ltu"].includes(country)) return "Lithuania";
  if (["estonia", "ee", "est"].includes(country)) return "Estonia";
  return "";
};

const COUNTRY_OPTIONS = ["All", "Latvia", "Lithuania", "Estonia"];

/** What Parkhi says for one row — see cammyPropertyCaseSummary.js. */
const summaryForPropertyRow = (caseItem) =>
  summarizePropertyCaseForCammy({
    address: caseItem.address,
    displayId: caseItem.displayId,
    country: caseItem.country,
    statusText: getStatusInfo(parseInt(caseItem.status, 10)).text,
    receivedAt: formatClearlineDateTime(caseItem.createdAt),
  });
const PAGE_SIZE = 15;

function getLast30DayRange() {
  const today = new Date();
  const from = new Date(today);
  from.setDate(from.getDate() - 30);
  return {
    startDate: from.toISOString().split("T")[0],
    endDate: today.toISOString().split("T")[0],
  };
}

export default function PropertyCasesRedesign({ detailsBasePath = "/redesign/property-cases" }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cases, setCases] = useState([]);
  const [totalInspections, setTotalInspections] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const nextOffsetRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const [selectedCountry, setSelectedCountry] = useState("All");
  const [startDate, setStartDate] = useState(() => getLast30DayRange().startDate);
  const [endDate, setEndDate] = useState(() => getLast30DayRange().endDate);
  const [searchQuery, setSearchQuery] = useState("");

  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const countryDropdownRef = useRef(null);
  const datePickerRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) setShowCountryDropdown(false);
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) setShowDatePicker(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Loaded alongside the cases to match the portal's own request set; their
  // payloads aren't surfaced in the UI yet.
  useEffect(() => {
    Promise.allSettled([
      getPropertyClientDemo(),
      getPropertyClientConfig(),
      getPropertyUseridList(),
    ]).then((results) => {
      results.forEach((r) => {
        if (r.status === "rejected") {
          console.warn("[PropertyCasesRedesign] side-load failed:", r.reason?.message);
        }
      });
    });
  }, []);

  const fetchCases = useCallback(
    async (isLoadingMoreCall = false, isSilent = false) => {
      try {
        if (!isSilent) {
          if (isLoadingMoreCall) setIsLoadingMore(true);
          else setLoading(true);
        }

        const response = await getPropertyAssessmentList({
          start_date: startDate || undefined,
          stop_date: endDate || undefined,
          per_page_limit: PAGE_SIZE,
          countryname: getCountryApiValue(selectedCountry),
          offset: isLoadingMoreCall ? nextOffsetRef.current || 0 : 0,
        });

        const { rows, total, nextOffset } = toPropertyList(response);
        nextOffsetRef.current = nextOffset;
        if (total != null) setTotalInspections(total);

        setCases((prev) => (isLoadingMoreCall ? [...prev, ...rows] : rows));
        setError(null);
      } catch (err) {
        console.error("Error fetching property cases:", err);
        setError("Failed to load property cases");
      } finally {
        if (!isSilent) {
          if (isLoadingMoreCall) setIsLoadingMore(false);
          else setLoading(false);
        }
      }
    },
    [startDate, endDate, selectedCountry],
  );

  useEffect(() => {
    nextOffsetRef.current = null;
    fetchCases(false);
  }, [fetchCases]);

  useEffect(() => {
    const hasProcessingCases = cases.some(
      (c) => getStatusInfo(parseInt(c.status, 10)).text === "In-Process",
    );
    if (hasProcessingCases) {
      pollingIntervalRef.current = setInterval(() => fetchCases(false, true), 5000);
    }
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [cases, fetchCases]);

  useEffect(() => {
    const handleScroll = () => {
      const container = scrollContainerRef.current;
      if (!container) return;
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (
        scrollHeight - (scrollTop + clientHeight) <= 200 &&
        !isLoadingMore &&
        !loading &&
        nextOffsetRef.current != null
      ) {
        fetchCases(true);
      }
    };
    const container = document.getElementById("property-cases-redesign-list");
    if (container) {
      scrollContainerRef.current = container;
      container.addEventListener("scroll", handleScroll, { passive: true });
    }
    return () => {
      if (scrollContainerRef.current) scrollContainerRef.current.removeEventListener("scroll", handleScroll);
    };
  }, [isLoadingMore, loading, fetchCases]);

  const resetFilters = () => {
    setSelectedCountry("All");
    const defaultRange = getLast30DayRange();
    setStartDate(defaultRange.startDate);
    setEndDate(defaultRange.endDate);
    setSearchQuery("");
  };

  const setDateShortcut = (days) => {
    const today = new Date();
    const pastDate = new Date();
    pastDate.setDate(today.getDate() - days);
    const fmt = (d) => d.toISOString().split("T")[0];
    setStartDate(fmt(pastDate));
    setEndDate(fmt(today));
  };

  // See filterRowsByCountry (propertyAssessment.js) for why this re-applies
  // the country filter client-side on top of the server-side countryname
  // param above.
  const countryFiltered = filterRowsByCountry(cases, selectedCountry);

  const query = searchQuery.trim().toLowerCase();
  const filteredCases = query
    ? countryFiltered.filter(
        (c) =>
          c.ref_num?.toLowerCase().includes(query) ||
          c.address?.toLowerCase().includes(query),
      )
    : countryFiltered;

  /**
   * Parkhi narrates whichever row is under the pointer — mirrors
   * CasesRedesign.js's handleRowHoverStart/handleRowClick, adapted to this
   * list's own row shape (address/displayId, no warnings at list level).
   */
  const { say: sayToCammy, clear: clearCammy } = useCammySay();
  const handleRowHoverStart = useCallback(
    (caseItem, event) => {
      const rect = event?.currentTarget?.getBoundingClientRect?.();
      sayToCammy(summaryForPropertyRow(caseItem), {
        source: "hover",
        leanY: rect ? rect.top + rect.height / 2 : undefined,
      });
    },
    [sayToCammy],
  );
  const handleRowHoverEnd = useCallback(() => clearCammy("hover"), [clearCammy]);
  // Leaving the list shouldn't leave a hover summary stuck on screen; a
  // click's summary has its own 5-second timer and is left alone.
  useEffect(() => () => clearCammy("hover"), [clearCammy]);

  const handleCaseClick = (caseItem, event) => {
    if (getStatusInfo(parseInt(caseItem.status, 10)).text === "In-Process") return;
    if (!caseItem.ref_num) return;
    const rect = event?.currentTarget?.getBoundingClientRect?.();
    sayToCammy(summaryForPropertyRow(caseItem), {
      source: "click",
      seconds: 5,
      leanY: rect ? rect.top + rect.height / 2 : undefined,
    });
    // New tab, same as every other case list in the app — a new tab gets no
    // router state, so the detail page's Back button correctly hides itself
    // (see src/utils/backNavigation.js).
    window.open(
      withCountryParam(`${detailsBasePath}/${encodeRefNum(caseItem.ref_num)}`, caseItem.country),
      "_blank",
    );
  };

  const pageShellClass = "flex h-full min-h-0 w-full flex-col bg-cl-bg px-4 py-4 sm:px-8 sm:py-[26px] font-clearline text-cl-text";
  const filterPillClass =
    "flex items-center gap-[7px] rounded-control border border-cl-control bg-cl-surface px-3 py-[9px] text-[12.5px] font-medium text-cl-secondary transition-colors hover:border-cl-faint";
  const dropdownPanelClass =
    "absolute left-0 top-[calc(100%+6px)] z-dropdown min-w-[200px] overflow-y-auto rounded-tile border border-cl-card bg-cl-surface py-1 shadow-[0_8px_24px_rgba(19,23,32,0.10)]";
  const dropdownItemClass = "cursor-pointer px-3 py-2 text-left text-[12.5px] text-cl-secondary transition-colors hover:bg-cl-tint hover:text-cl-accent";
  const gridCols = "72px 1.6fr 150px 150px 150px";

  const header = (
    <div className="mb-5 flex items-start justify-between gap-4">
      <div>
        <h1 className="text-[22px] font-bold leading-tight tracking-[-0.02em] text-cl-text">Property Cases</h1>
        {/* Hidden once the visible list is empty — the report's own total
            can disagree with "0 shown" and reads as a contradiction sitting
            right above "No cases found". */}
        {!(!loading && filteredCases.length === 0) && (
          <p className="mt-[3px] text-[12.5px] text-cl-muted">
            {loading && totalInspections == null
              ? "Loading inspection totals…"
              : `A total of ${(totalInspections ?? 0).toLocaleString()} inspections`}
          </p>
        )}
      </div>
    </div>
  );

  if (error) {
    return (
      <div className={pageShellClass}>
        {header}
        <div className="flex flex-1 items-center justify-center rounded-card border border-cl-card bg-cl-surface">
          <div className="max-w-sm text-center">
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-pill bg-[#fbe9e9]">
              <AlertTriangle className="h-5 w-5 text-[#c22f2f]" />
            </div>
            <p className="text-[14px] font-semibold text-cl-text">{error}</p>
            <button
              type="button"
              onClick={() => fetchCases(false)}
              className="mt-4 rounded-control bg-cl-accent px-4 py-[9px] text-[13px] font-semibold text-white shadow-cl-primary transition-colors hover:bg-cl-accent-hover"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={pageShellClass}>
      {header}

      {/* Filter bar */}
      <div className="mb-[18px] flex flex-wrap items-center gap-[10px]">
        <div className="relative flex w-full max-w-[320px] flex-1 items-center gap-[9px] rounded-control border border-cl-control bg-cl-surface px-3 py-[9px] focus-within:border-cl-accent">
          <Search className="h-[14px] w-[14px] flex-none text-cl-faint" />
          <input
            type="text"
            placeholder="Search inspection ID or address"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="min-w-0 flex-1 bg-transparent text-[13px] text-cl-text outline-none placeholder:text-cl-faint"
          />
          {searchQuery && (
            <button type="button" onClick={() => setSearchQuery("")} title="Clear search" className="flex-none text-cl-faint hover:text-cl-secondary">
              <X className="h-[14px] w-[14px]" />
            </button>
          )}
        </div>

        <div ref={countryDropdownRef} className="relative">
          <button
            type="button"
            onClick={() => {
              setShowCountryDropdown((v) => !v);
              setShowDatePicker(false);
            }}
            className={filterPillClass}
          >
            Country
            <span className="font-semibold text-cl-accent">{selectedCountry}</span>
            <ChevronDown className="h-[10px] w-[10px] text-cl-faint" />
          </button>
          {showCountryDropdown && (
            <div className={`${dropdownPanelClass} max-h-[320px]`}>
              {COUNTRY_OPTIONS.map((country) => (
                <div
                  key={country}
                  className={`${dropdownItemClass} flex items-center gap-[8px]`}
                  onClick={() => { setSelectedCountry(country); setShowCountryDropdown(false); }}
                >
                  {country === "All" ? (
                    <span className="h-[13px] w-[18px] flex-none" />
                  ) : (
                    <CountryFlag country={country} />
                  )}
                  {country}
                </div>
              ))}
            </div>
          )}
        </div>

        <div ref={datePickerRef} className="relative">
          <button
            type="button"
            onClick={() => {
              setShowDatePicker((v) => !v);
              setShowCountryDropdown(false);
            }}
            className={filterPillClass}
          >
            <span className={startDate && endDate ? "font-semibold text-cl-accent" : ""}>
              {startDate && endDate ? `${startDate} - ${endDate}` : "Select dates"}
            </span>
            <ChevronDown className="h-[10px] w-[10px] text-cl-faint" />
          </button>
          {showDatePicker && (
            <div className="absolute left-0 top-[calc(100%+6px)] z-dropdown w-[300px] rounded-tile border border-cl-card bg-cl-surface p-4 shadow-[0_8px_24px_rgba(19,23,32,0.10)]">
              <div className="mb-4 flex gap-2">
                {[{ days: 1, label: "Last day" }, { days: 7, label: "Last 7 days" }, { days: 30, label: "Last 30 days" }].map((s) => (
                  <button key={s.days} type="button" onClick={() => setDateShortcut(s.days)} className="flex-1 rounded-tile border border-cl-control bg-cl-surface px-2 py-[6px] text-[11px] font-medium text-cl-secondary hover:border-cl-accent hover:text-cl-accent">
                    {s.label}
                  </button>
                ))}
              </div>
              <label className="mb-1 block text-[9.5px] font-semibold uppercase tracking-[0.06em] text-cl-label">From</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mb-3 w-full rounded-tile border border-cl-control bg-cl-surface px-3 py-2 text-[12.5px] text-cl-text outline-none focus:border-cl-accent" />
              <label className="mb-1 block text-[9.5px] font-semibold uppercase tracking-[0.06em] text-cl-label">To</label>
              <input type="date" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)} className="mb-4 w-full rounded-tile border border-cl-control bg-cl-surface px-3 py-2 text-[12.5px] text-cl-text outline-none focus:border-cl-accent" />
              <button type="button" onClick={() => setShowDatePicker(false)} className="w-full rounded-tile bg-cl-accent px-3 py-2 text-[12px] font-semibold text-white shadow-cl-primary hover:bg-cl-accent-hover">
                Apply
              </button>
            </div>
          )}
        </div>

        <button type="button" onClick={resetFilters} className="ml-auto rounded-control border border-cl-control bg-cl-surface px-3 py-[9px] text-[12.5px] font-medium text-cl-secondary hover:border-cl-faint">
          Reset filters
        </button>
      </div>

      {/* Table card */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-card border border-cl-card bg-cl-surface">
        <div className="hidden flex-none items-center border-b border-cl-head px-4 py-[10px] text-[10.5px] font-semibold uppercase tracking-[0.07em] text-cl-label lg:grid" style={{ gridTemplateColumns: gridCols }}>
          <div>Property</div>
          <div>Inspection ID</div>
          <div>Country</div>
          <div>Status</div>
          <div className="text-right">Received</div>
        </div>

        {loading ? (
          <div className="flex flex-1 items-center justify-center py-20">
            <div className="text-center">
              <img src={loadingIcon} alt="" className="mx-auto mb-3 h-7 w-7 animate-spin" />
              <p className="text-[12.5px] text-cl-muted">Loading cases…</p>
            </div>
          </div>
        ) : filteredCases.length === 0 ? (
          <div className="flex flex-1 items-center justify-center py-20">
            <div className="text-center">
              <p className="text-[14px] font-semibold text-cl-text">No cases found</p>
              <p className="mt-1 text-[12.5px] text-cl-muted">
                No inspections match the current filters.
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-4 rounded-control bg-cl-accent px-5 py-[9px] text-[13px] font-semibold text-white shadow-cl-primary transition-colors hover:bg-cl-accent-hover"
              >
                Reset all filters
              </button>
            </div>
          </div>
        ) : (
          <div id="property-cases-redesign-list" className="min-h-0 flex-1 overflow-y-auto">
            {filteredCases.map((caseItem, index) => {
              const statusText = getStatusInfo(parseInt(caseItem.status, 10)).text;
              const isProcessing = statusText === "In-Process";
              const chip = getClearlineStatusChip(statusText);
              return (
                <div
                  key={caseItem.ref_num || index}
                  role="button"
                  tabIndex={isProcessing ? -1 : 0}
                  onClick={(event) => handleCaseClick(caseItem, event)}
                  onMouseEnter={(event) => handleRowHoverStart(caseItem, event)}
                  onMouseLeave={handleRowHoverEnd}
                  className={`flex flex-col gap-[6px] border-b border-cl-row px-4 py-3 transition-colors lg:grid lg:items-center lg:gap-0 lg:py-[10px] ${isProcessing ? "cursor-default opacity-60" : "cursor-pointer hover:bg-cl-hover"}`}
                  style={{ gridTemplateColumns: gridCols }}
                >
                  <div>
                    <div className="flex h-10 w-10 items-center justify-center rounded-thumb bg-cl-bg">
                      {/* The list endpoint carries no image — car_images comes
                          back {} for property and there is no picture field —
                          so thumbnailUrl was always null and every row fell
                          back to the building glyph. CaseThumb fetches one per
                          row using the client's own listThumbnail strategy,
                          which is what the motor list already does. */}
                      {caseItem.ref_num ? (
                        <CaseThumb
                          refNum={caseItem.ref_num}
                          className="h-full w-full rounded-thumb object-cover"
                        />
                      ) : (
                        <Building2 className="h-5 w-5 text-cl-faint" />
                      )}
                    </div>
                  </div>
                  <div className="min-w-0 pr-3">
                    <div className="truncate text-[13.5px] font-semibold tracking-[-0.01em] text-cl-text">
                      {caseItem.address || "—"}
                    </div>
                    <div className="mt-[2px] truncate text-[11px] font-normal text-cl-faint">
                      {caseItem.displayId}
                    </div>
                  </div>
                  <div className="flex min-w-0 items-center gap-[7px] pr-3 text-[12.5px] text-cl-secondary">
                    <CountryFlag country={caseItem.country} />
                    <span className="truncate">{caseItem.country || "—"}</span>
                  </div>
                  <div>
                    <span className="inline-flex items-center gap-[6px] rounded-pill px-[10px] py-1 text-[11.5px] font-semibold" style={{ background: chip.bg, color: chip.fg }}>
                      <span className="h-[6px] w-[6px] rounded-pill" style={{ background: chip.fg }} />
                      {statusText}
                    </span>
                  </div>
                  <div className="text-right text-[12px] text-cl-muted">
                    {formatClearlineDateTime(caseItem.createdAt)}
                  </div>
                </div>
              );
            })}
            {isLoadingMore && (
              <div className="flex items-center justify-center gap-2 py-6 text-[12px] text-cl-muted">
                <img src={loadingIcon} alt="" className="h-4 w-4 animate-spin" />
                Loading more cases…
              </div>
            )}
            {nextOffsetRef.current == null && !isLoadingMore && filteredCases.length > 0 && (
              <div className="py-6 text-center text-[12px] text-cl-disabled">You've reached the end of the list</div>
            )}
          </div>
        )}

        {/* Card footer — same "Showing X of Y" shape as the motor list's,
            X being what has loaded and been filtered into view so far
            (infinite scroll, so more than one page can be in it), Y the
            server's own total for the current filters. */}
        <div className="flex flex-none items-center justify-between border-t border-cl-head px-4 py-3 text-[12px] text-cl-label">
          <div>
            {loading
              ? "Loading…"
              : `Showing ${filteredCases.length === 0 ? 0 : 1}–${filteredCases.length}${
                  totalInspections != null ? ` of ${totalInspections.toLocaleString()}` : ""
                }`}
          </div>
        </div>
      </div>
    </div>
  );
}
